import { useEffect, useRef, useState } from "react";
import { Star, Check, ChevronDown, Search, Loader2, X } from "lucide-react";
import { C } from "@/theme";

// ------------------------------- Button -------------------------------------
export function Button({ label, onClick, variant = "primary", disabled, loading, testID, small, type = "button", full = true }) {
  const bg =
    variant === "primary" ? C.brandPrimary : variant === "danger" ? C.error : variant === "secondary" ? C.surfaceTertiary : "transparent";
  const color = variant === "secondary" || variant === "ghost" ? C.onSurface : C.onBrandPrimary;
  return (
    <button
      type={type}
      data-testid={testID}
      onClick={onClick}
      disabled={disabled || loading}
      style={{ background: bg, color, width: full ? "100%" : "auto" }}
      className={`inline-flex items-center justify-center gap-2 font-bold rounded-xl transition-all duration-150 active:scale-[0.98] hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed ${small ? "h-9 px-4 text-[13px]" : "h-12 px-5 text-[15px]"}`}
    >
      {loading ? <Loader2 size={18} className="animate-spin" /> : label}
    </button>
  );
}

// -------------------------------- Badge -------------------------------------
const BADGE = {
  success: { bg: "#DCF3E8", fg: C.success },
  warning: { bg: "#FCEFD6", fg: C.warning },
  error: { bg: "#FADBDB", fg: C.error },
  info: { bg: C.surfaceTertiary, fg: C.info },
  neutral: { bg: C.surfaceTertiary, fg: C.onSurfaceTertiary },
  brand: { bg: C.brandTertiary, fg: C.onBrandTertiary },
};
export function Badge({ label, variant = "neutral", testID }) {
  const s = BADGE[variant] ?? BADGE.neutral;
  return (
    <span data-testid={testID} style={{ background: s.bg, color: s.fg }} className="inline-block px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap">
      {label}
    </span>
  );
}

// -------------------------------- Card --------------------------------------
export function Card({ children, className = "", testID, style }) {
  return (
    <div data-testid={testID} style={{ background: C.surfaceSecondary, borderColor: C.border, ...style }} className={`rounded-2xl border p-4 ${className}`}>
      {children}
    </div>
  );
}

export function SectionTitle({ children }) {
  return <h3 style={{ color: C.onSurface }} className="text-base font-extrabold">{children}</h3>;
}
export function CardSub({ children }) {
  return <p style={{ color: C.muted }} className="text-[13px] mt-0.5">{children}</p>;
}

// ------------------------------ StarRating ----------------------------------
export function StarRating({ value, onChange, size = 18, testID }) {
  return (
    <div className="flex gap-0.5" data-testid={testID}>
      {[1, 2, 3, 4, 5].map((s) => {
        const filled = s <= value;
        const node = <Star size={size} fill={filled ? C.warning : "none"} color={filled ? C.warning : C.borderStrong} />;
        if (!onChange) return <span key={s}>{node}</span>;
        return (
          <button type="button" key={s} data-testid={`${testID}-star-${s}`} onClick={() => onChange(s)} className="hover:scale-110 transition-transform">
            {node}
          </button>
        );
      })}
    </div>
  );
}

// --------------------------- States -----------------------------------------
export function LoadingState({ testID }) {
  return (
    <div data-testid={testID ?? "loading-state"} className="flex items-center justify-center py-20">
      <Loader2 size={34} className="animate-spin" color={C.brandPrimary} />
    </div>
  );
}
export function EmptyState({ icon, title, subtitle, testID }) {
  return (
    <div data-testid={testID ?? "empty-state"} className="flex flex-col items-center justify-center py-16 gap-2 text-center">
      {icon}
      <p style={{ color: C.onSurface }} className="text-base font-bold mt-2">{title}</p>
      {subtitle ? <p style={{ color: C.muted }} className="text-sm max-w-sm">{subtitle}</p> : null}
    </div>
  );
}
export function ErrorState({ onRetry, testID }) {
  return (
    <div data-testid={testID ?? "error-state"} className="flex flex-col items-center justify-center py-16 gap-3 text-center">
      <p style={{ color: C.onSurface }} className="text-base font-bold">Something went wrong</p>
      <p style={{ color: C.muted }} className="text-sm">Unable to load data.</p>
      {onRetry ? <div className="w-28"><Button label="Retry" variant="secondary" onClick={onRetry} small /></div> : null}
    </div>
  );
}

// ---------------------------- Inputs ----------------------------------------
export function Field({ label, value, onChange, placeholder, type = "text", multiline, disabled, testID, min, max }) {
  const common = {
    value: value ?? "",
    onChange: (e) => onChange && onChange(e.target.value),
    placeholder,
    disabled,
    ...(min != null ? { min } : {}),
    ...(max != null ? { max } : {}),
    "data-testid": testID,
    style: { background: C.surfaceTertiary, borderColor: C.border, color: C.onSurface },
  };
  return (
    <div className="flex flex-col gap-2">
      {label ? <label style={{ color: C.onSurfaceTertiary }} className="text-[13px] font-bold">{label}</label> : null}
      {multiline ? (
        <textarea {...common} rows={3} className="rounded-xl border px-3.5 py-3 text-[15px] outline-none focus:border-[#2563C9] disabled:opacity-60 resize-y min-h-[90px]" />
      ) : (
        <input {...common} type={type} className="rounded-xl border px-3.5 py-3 text-[15px] outline-none focus:border-[#2563C9] disabled:opacity-60" />
      )}
    </div>
  );
}

export function OptionSelector({ label, options, value, onChange, testID }) {
  return (
    <div className="flex flex-col gap-2">
      {label ? <label style={{ color: C.onSurfaceTertiary }} className="text-[13px] font-bold">{label}</label> : null}
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = o === value;
          return (
            <button type="button" key={o} data-testid={`${testID}-${o}`} onClick={() => onChange(o)}
              style={{ background: active ? C.brandPrimary : C.surfaceTertiary, borderColor: active ? C.brandPrimary : C.border, color: active ? C.onBrandPrimary : C.onSurfaceTertiary }}
              className="px-3.5 py-2 rounded-full border text-[13px] font-semibold transition-colors">
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function MultiOptionSelector({ label, options, values, onChange, testID }) {
  const toggle = (o) => onChange(values.includes(o) ? values.filter((v) => v !== o) : [...values, o]);
  return (
    <div className="flex flex-col gap-2">
      {label ? <label style={{ color: C.onSurfaceTertiary }} className="text-[13px] font-bold">{label}</label> : null}
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = values.includes(o);
          return (
            <button type="button" key={o} data-testid={`${testID}-${o}`} onClick={() => toggle(o)}
              style={{ background: active ? C.brandPrimary : C.surfaceTertiary, borderColor: active ? C.brandPrimary : C.border, color: active ? C.onBrandPrimary : C.onSurfaceTertiary }}
              className="px-3.5 py-2 rounded-full border text-[13px] font-semibold transition-colors">
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function Checkbox({ label, checked, onChange, testID, color = C.success }) {
  return (
    <button type="button" data-testid={testID} onClick={onChange} className="flex items-center gap-3 py-2.5 text-left w-full">
      <span style={{ background: checked ? color : "transparent", borderColor: checked ? color : C.borderStrong }} className="w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0">
        {checked ? <Check size={14} color="#fff" strokeWidth={3} /> : null}
      </span>
      <span style={{ color: C.onSurface }} className="text-[15px] font-semibold">{label}</span>
    </button>
  );
}

// --------------------------- Segmented / Chips ------------------------------
export function Segmented({ options, value, onChange, testID }) {
  return (
    <div style={{ background: C.surfaceTertiary }} className="inline-flex rounded-xl p-1 gap-1" data-testid={testID}>
      {options.map((o) => {
        const active = o.key === value;
        return (
          <button type="button" key={o.key} data-testid={`${testID}-${o.key}`} onClick={() => onChange(o.key)}
            style={{ background: active ? C.surfaceSecondary : "transparent", color: active ? C.onSurface : C.muted }}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${active ? "shadow-sm" : ""}`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Chips({ options, value, onChange, testID }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testID}>
      {options.map((o) => {
        const active = o.key === value;
        return (
          <button type="button" key={o.key} data-testid={`${testID}-${o.key}`} onClick={() => onChange(o.key)}
            style={{ background: active ? C.brandPrimary : C.surfaceTertiary, borderColor: active ? C.brandPrimary : C.border, color: active ? C.onBrandPrimary : C.onSurfaceTertiary }}
            className="h-9 px-3.5 rounded-full border text-[13px] font-semibold transition-colors">
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ------------------------------ Picker --------------------------------------
// Searchable dropdown. options: [{ key, label, sub }]
export function Picker({ label, options, value, onSelect, placeholder = "Select", searchable, testID }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const selected = options.find((o) => o.key === value);
  const filtered = searchable && q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;
  return (
    <div className="flex flex-col gap-2" ref={ref}>
      {label ? <label style={{ color: C.onSurfaceTertiary }} className="text-[13px] font-bold">{label}</label> : null}
      <div className="relative">
        <button type="button" data-testid={testID} onClick={() => setOpen((v) => !v)}
          style={{ background: C.surfaceTertiary, borderColor: C.border, color: selected ? C.onSurface : C.muted }}
          className="w-full flex items-center justify-between rounded-xl border px-3.5 h-12 text-[15px] font-semibold">
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          <ChevronDown size={18} color={C.muted} />
        </button>
        {open ? (
          <div style={{ background: C.surfaceSecondary, borderColor: C.border }} className="absolute z-30 mt-1 w-full rounded-xl border shadow-lg max-h-72 overflow-auto p-1.5" data-testid={`${testID}-list`}>
            {searchable ? (
              <div style={{ background: C.surfaceTertiary, borderColor: C.border }} className="flex items-center gap-2 rounded-lg border px-2.5 h-10 mb-1 sticky top-0">
                <Search size={16} color={C.muted} />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="flex-1 bg-transparent outline-none text-sm" style={{ color: C.onSurface }} />
              </div>
            ) : null}
            {filtered.length === 0 ? (
              <p style={{ color: C.muted }} className="text-sm p-3">No options</p>
            ) : (
              filtered.map((o) => (
                <button type="button" key={o.key} data-testid={`${testID}-opt-${o.key}`}
                  onClick={() => { onSelect(o.key); setOpen(false); setQ(""); }}
                  style={{ background: o.key === value ? C.brandTertiary : "transparent" }}
                  className="w-full text-left rounded-lg px-3 py-2.5 hover:bg-[#EAF0F8] flex items-center justify-between gap-2">
                  <span>
                    <span style={{ color: C.onSurface }} className="block text-[15px] font-semibold">{o.label}</span>
                    {o.sub ? <span style={{ color: C.muted }} className="block text-xs">{o.sub}</span> : null}
                  </span>
                  {o.key === value ? <Check size={16} color={C.brandPrimary} /> : null}
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ------------------------------ Modal ---------------------------------------
export function Modal({ open, onClose, title, children, testID }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.45)" }} onMouseDown={onClose}>
      <div data-testid={testID} onMouseDown={(e) => e.stopPropagation()} style={{ background: C.surfaceSecondary }} className="w-full max-w-md rounded-2xl p-5 shadow-2xl">
        {title ? (
          <div className="flex items-center justify-between mb-2">
            <h3 style={{ color: C.onSurface }} className="text-lg font-extrabold">{title}</h3>
            <button onClick={onClose}><X size={20} color={C.muted} /></button>
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}

export function ConfirmDialog({ open, title, message, confirmLabel = "Delete", onConfirm, onCancel, danger = true }) {
  return (
    <Modal open={open} onClose={onCancel} testID="confirm-modal">
      <h3 style={{ color: C.onSurface }} className="text-lg font-extrabold">{title}</h3>
      <p style={{ color: C.muted }} className="text-sm mt-2 leading-relaxed">{message}</p>
      <div className="flex gap-3 mt-5">
        <Button label="Cancel" variant="secondary" onClick={onCancel} testID="confirm-cancel" />
        <Button label={confirmLabel} variant={danger ? "danger" : "primary"} onClick={onConfirm} testID="confirm-ok" />
      </div>
    </Modal>
  );
}

// ----------------------- FormScreen (routed form) ---------------------------
export function FormScreen({ title, children, onSave, saving, saveDisabled, onClose, saveLabel = "Save", wide }) {
  return (
    <div className={`mx-auto ${wide ? "max-w-3xl" : "max-w-xl"}`}>
      <div className="flex items-center justify-between mb-5">
        <h1 style={{ color: C.onSurface }} className="text-2xl font-extrabold font-display">{title}</h1>
        <button data-testid="form-close" onClick={onClose} style={{ background: C.surfaceSecondary, borderColor: C.border }} className="w-10 h-10 rounded-xl border flex items-center justify-center">
          <X size={18} color={C.onSurface} />
        </button>
      </div>
      <div className="flex flex-col gap-4">{children}</div>
      <div className="flex gap-3 mt-6 sticky bottom-0 py-4" style={{ background: C.pageBg }}>
        <Button label="Cancel" variant="secondary" onClick={onClose} testID="form-cancel" />
        <Button label={saveLabel} onClick={onSave} loading={saving} disabled={saveDisabled} testID="form-save" />
      </div>
    </div>
  );
}
